import React, { useCallback, useMemo } from 'react';
import CableSVGPath from './CableSVGPath';
import { renderTerminalSchematic } from '../../utils/terminalRenderLogic';
import { hasCustomTerminalGraphic, formatSezione, splitText, getSiglaturaPerStampa, extractSiglaturaFromCollegamento, estimateTextWidth, getEffectiveSectionForDisplay } from '../../utils/cableLogic';
import { SECTION_SCALE_MAP, BASE_TERMINAL_SCALE, MIN_TERMINAL_SCALE, MAX_TERMINAL_SCALE, TERMINAL_SCALE_PROPORTIONAL_FACTOR } from '../../constants/terminalConstants';

// ✅ Funzione per verificare se è un cavo di terra
const isEarthWire = (colore) => {
    return colore === 'GI/VE' || colore === 'YE/GN';
};

// ✅ Funzione per verificare se una siglatura è valida (non placeholder)
const isValidSiglatura = (sig) => {
    if (!sig || String(sig).trim() === '') return false;
    const sigTrimmed = String(sig).trim();
    // Escludi placeholder e siglature di terra
    const invalidSigs = ['[Senza siglatura]', '[No marking]', 'TERRA', 'GND', 'EARTH'];
    return !invalidSigs.includes(sigTrimmed);
};

// Componente per visualizzazione cavi multipli
const MultipleCableVisualization = ({ cables, siglatura, hoveredCable, setHoveredCable, isMobile, settings, sheath = null, availableSheaths = [] }) => {
    const calculateMobileVerticalSpacing = useCallback((cables) => {
        if (cables.length === 0) return { terminalSpacing: [], totalHeight: 200 };

        const MIN_SPACING = 50;
        const MAX_SPACING = 200;
        const PADDING_TOP_BOTTOM = 60;

        const totalRealLength = cables.reduce((sum, cable) => sum + (cable.lunghezza || 100), 0);

        const spacings = cables.map(cable => {
            const realLength = cable.lunghezza || 100;

            const normalized = realLength / totalRealLength;
            const amplified = Math.pow(normalized, 0.8); // Radice per amplificare i piccoli valori

            const proportional = amplified * (cables.length * 140); // Aumentato moltiplicatore
            return Math.max(Math.min(proportional, MAX_SPACING), MIN_SPACING);
        });

        const totalHeight = spacings.reduce((sum, spacing) => sum + spacing, 0) + PADDING_TOP_BOTTOM;

        return { terminalSpacing: spacings, totalHeight };
    }, []);

    const calculateSpaceAndLengths = useCallback((cables, svgWidth) => {
        if (cables.length === 0) return { totalSpace: 0, distributedLengths: [] };

        const MARGIN = 100;
        const totalAvailableSpace = svgWidth - (2 * MARGIN);
        const totalRealLength = cables.reduce((sum, cable) => sum + (cable.lunghezza || 100), 0);
        const MIN_VISUAL_LENGTH = 40;
        const minTotalLength = cables.length * MIN_VISUAL_LENGTH;

        const spaceToDistribute = Math.max(totalAvailableSpace, minTotalLength);

        const distributedLengths = cables.map(cable => {
            const realLength = cable.lunghezza || 100;
            const proportional = (realLength / totalRealLength) * spaceToDistribute;
            return Math.max(proportional, MIN_VISUAL_LENGTH);
        });

        return { totalSpace: spaceToDistribute, distributedLengths };
    }, []);

    const calculateDesktopTerminalPositions = useCallback((connections, cables, svgWidth) => {
        const entries = Object.entries(connections);
        const singles = entries.filter(([_, data]) => data.cables.length === 1);
        const multiples = entries.filter(([_, data]) => data.cables.length > 1);

        const ordered = [
            ...singles.slice(0, Math.ceil(singles.length / 2)),
            ...multiples,
            ...singles.slice(Math.ceil(singles.length / 2))
        ];

        const { totalSpace, distributedLengths } = calculateSpaceAndLengths(cables, svgWidth);
        const startX = (svgWidth - totalSpace) / 2;

        // ✅ Posizioni separate per desktop: dove arrivano i cavi (scritte) e dove sono i terminali
        const DESKTOP_CABLE_END_Y = 80; // Posizione Y dove arrivano i cavi (per scritte di collegamento)
        const DESKTOP_TERMINAL_Y = 85;  // Posizione Y dove vengono renderizzati i terminali (più in alto)

        if (distributedLengths.length === 0) {
            // Fallback alla logica originale se non ci sono lunghezze
            const spacing = svgWidth / (ordered.length + 1);
            ordered.forEach(([connName, data], i) => {
                connections[connName].x = spacing * (i + 1);
                connections[connName].y = DESKTOP_CABLE_END_Y; // Posizione Y per le scritte (dove arrivano i cavi)
                connections[connName].terminalY = DESKTOP_TERMINAL_Y; // Posizione Y separata per i terminali
            });
            return;
        }

        let currentX = startX;
        const terminalPositions = [currentX];

        distributedLengths.forEach(length => {
            currentX += length;
            terminalPositions.push(currentX);
        });

        ordered.forEach(([connName, data], i) => {
            if (i < terminalPositions.length) {
                connections[connName].x = terminalPositions[i];
                connections[connName].y = DESKTOP_CABLE_END_Y; // Posizione Y per le scritte (dove arrivano i cavi)
                connections[connName].terminalY = DESKTOP_TERMINAL_Y; // Posizione Y separata per i terminali
            }
        });

    }, [calculateSpaceAndLengths]);

    const { totalHeight } = calculateMobileVerticalSpacing(cables);
    const svgWidth = isMobile ? 400 : 800;
    const svgHeight = isMobile ? totalHeight : 250;

    const connections = useMemo(() => {
        const conn = {};

        cables.forEach((cable, idx) => {
            ['collegamentoA', 'collegamentoB'].forEach((side, sideIdx) => {
                const collegamento = cable[side];

                // ✅ Per collegamenti vuoti o solo spazi, crea una chiave unica
                // in modo che vengano renderizzati ma non raggruppati insieme
                const isEmpty = !collegamento || String(collegamento).trim() === '';
                const connectionKey = isEmpty
                    ? `__empty_${cable.originalIndex}_${sideIdx === 0 ? 'A' : 'B'}`
                    : collegamento;

                if (!conn[connectionKey]) {
                    conn[connectionKey] = {
                        cables: [],
                        terminale: null,
                        collegamento: collegamento, // Salviamo il valore originale
                        isEmpty: isEmpty, // Flag per sapere se è un collegamento vuoto
                        x: 0,
                        y: 0
                    };
                }
                conn[connectionKey].cables.push({
                    ...cable,
                    index: idx,
                    side: sideIdx === 0 ? 'A' : 'B'
                });
                if (!conn[connectionKey].terminale) {
                    conn[connectionKey].terminale = cable[sideIdx === 0 ? 'terminaleA' : 'terminaleB'];
                }
            });
        });

        if (isMobile) {
            const { terminalSpacing } = calculateMobileVerticalSpacing(cables);

            const entries = Object.entries(conn);
            const singles = entries.filter(([_, data]) => data.cables.length === 1);
            const multiples = entries.filter(([_, data]) => data.cables.length > 1);

            const ordered = [
                ...singles.slice(0, Math.ceil(singles.length / 2)),
                ...multiples,
                ...singles.slice(Math.ceil(singles.length / 2))
            ];

            let currentY = 40; // Start Y
            // ✅ Posizioni separate per mobile: dove arrivano i cavi (scritte) e dove sono i terminali
            const MOBILE_CABLE_END_X = 110; // Posizione X dove arrivano i cavi (per scritte di collegamento)
            const MOBILE_TERMINAL_X = 100;   // Posizione X dove vengono renderizzati i terminali (più a sinistra)

            ordered.forEach(([connName, data], i) => {
                conn[connName].y = currentY;
                conn[connName].x = MOBILE_CABLE_END_X; // Posizione per le scritte (dove arrivano i cavi)
                conn[connName].terminalX = MOBILE_TERMINAL_X; // Posizione separata per i terminali

                // Aggiungi la spaziatura proporzionale per il prossimo terminale
                if (i < terminalSpacing.length) {
                    currentY += terminalSpacing[i];
                }
            });
        } else {
            calculateDesktopTerminalPositions(conn, cables, svgWidth);
        }

        return conn;
    }, [cables, isMobile, svgWidth, svgHeight, calculateDesktopTerminalPositions]);

    const cablePositions = useMemo(() => {
        return cables.map((cable) => {
            // ✅ Ricostruiamo le chiavi corrette per cercare nella mappa connections
            const isEmptyA = !cable.collegamentoA || String(cable.collegamentoA).trim() === '';
            const keyA = isEmptyA
                ? `__empty_${cable.originalIndex}_A`
                : cable.collegamentoA;

            const isEmptyB = !cable.collegamentoB || String(cable.collegamentoB).trim() === '';
            const keyB = isEmptyB
                ? `__empty_${cable.originalIndex}_B`
                : cable.collegamentoB;

            if (isMobile) {
                const connA = connections[keyA];
                const connB = connections[keyB];
                if (connA && connB) {
                    return { x: 180, y: (connA.y + connB.y) / 2 };
                } else if (connA) {
                    return { x: 180, y: connA.y };
                } else if (connB) {
                    return { x: 180, y: connB.y };
                }
            } else {
                const connA = connections[keyA];
                const connB = connections[keyB];

                // Nota: In desktop la logica di fallback per connNameA/B non serve se usiamo le chiavi corrette

                if (connA && connB) {
                    return { x: (connA.x + connB.x) / 2, y: 200 };
                } else if (connA) {
                    const nextX = Object.values(connections)
                        .map(c => c.x)
                        .filter(x => x > connA.x)
                        .sort((a, b) => a - b)[0];
                    return { x: nextX ? (connA.x + nextX) / 2 : connA.x + 100, y: 200 };
                } else if (connB) {
                    const prevX = Object.values(connections)
                        .map(c => c.x)
                        .filter(x => x < connB.x)
                        .sort((a, b) => b - a)[0];
                    return { x: prevX ? (connB.x + prevX) / 2 : connB.x - 100, y: 200 };
                }
                return { x: svgWidth / 2, y: 200 };
            }
            return { x: 180, y: 100 }; // Fallback
        });
    }, [cables, connections, isMobile, svgWidth, svgHeight]);


    // ✅ Determina quali siglature devono essere renderizzate (solo per il cavo con sezione maggiore per ogni collegamento/terminale)
    // E calcola le posizioni per il rendering separato alla fine
    const siglatureToRender = useMemo(() => {
        const siglatureMap = new Map(); // key: "collegamento|terminale|side", value: { cable, siglatura, position }

        cables.forEach((cable, idx) => {
            // Non processare siglature per cavi di terra
            if (isEarthWire(cable.colore)) {
                return;
            }

            // ✅ Ricostruiamo le chiavi corrette per cercare nella mappa connections
            const isEmptyA = !cable.collegamentoA || String(cable.collegamentoA).trim() === '';
            const keyA_Conn = isEmptyA
                ? `__empty_${cable.originalIndex}_A`
                : cable.collegamentoA;

            const isEmptyB = !cable.collegamentoB || String(cable.collegamentoB).trim() === '';
            const keyB_Conn = isEmptyB
                ? `__empty_${cable.originalIndex}_B`
                : cable.collegamentoB;

            const connA = connections[keyA_Conn];
            const connB = connections[keyB_Conn];

            // ✅ Calcola posizioni terminali per le siglature (dove sono i terminali)
            const terminalAX = connA ? (connA.terminalX || connA.x) : null;
            const terminalAY = connA ? (isMobile ? connA.y : (connA.terminalY || connA.y)) : null;
            const terminalBX = connB ? (connB.terminalX || connB.x) : null;
            const terminalBY = connB ? (isMobile ? connB.y : (connB.terminalY || connB.y)) : null;

            // Calcola scala terminale per offset
            const calculateTerminalScale = (sezione, useAWG) => {
                const effectiveSection = getEffectiveSectionForDisplay(sezione, useAWG);

                // Se la sezione è esattamente nella mappa, usa quel valore
                if (SECTION_SCALE_MAP[effectiveSection]) {
                    return SECTION_SCALE_MAP[effectiveSection];
                }

                // Altrimenti calcola una scala proporzionale
                const proportionalScale = BASE_TERMINAL_SCALE + (effectiveSection / 100) * TERMINAL_SCALE_PROPORTIONAL_FACTOR;
                return Math.max(MIN_TERMINAL_SCALE, Math.min(MAX_TERMINAL_SCALE, proportionalScale));
            };

            const terminalScale = calculateTerminalScale(cable.sezione, settings.useAWG);

            // ✅ Offset diverso per mobile (orizzontale a destra) e desktop (verticale sopra)
            const horizontalOffset = isMobile ? terminalScale * 200 + 25 : 0; // Offset orizzontale per mobile
            const verticalOffset = isMobile ? 0 : terminalScale * 500 + 10; // Offset verticale per desktop

            // Lato A - ✅ Rimosso check su cable.collegamentoA per permettere rendering anche se vuoto
            if (cable.terminaleA && terminalAX !== null && terminalAY !== null) {
                const keyA = `${keyA_Conn}|${cable.terminaleA}|A`;
                const existing = siglatureMap.get(keyA);
                const effectiveSectionA = getEffectiveSectionForDisplay(cable.sezione, settings.useAWG);

                if (!existing || getEffectiveSectionForDisplay(existing.cable.sezione, settings.useAWG) < effectiveSectionA) {
                    let siglaturaA = '';
                    if (settings.marcaturePerCollegamento) {
                        siglaturaA = extractSiglaturaFromCollegamento(cable.collegamentoA) || '';
                    } else {
                        siglaturaA = cable.siglatura || '';
                    }

                    // ✅ Verifica che la siglatura sia valida (non placeholder)
                    if (isValidSiglatura(siglaturaA)) {
                        siglatureMap.set(keyA, {
                            cable,
                            siglatura: siglaturaA,
                            side: 'A',
                            index: idx,
                            position: {
                                x: terminalAX + horizontalOffset, // A destra in mobile, stessa X in desktop
                                y: terminalAY + verticalOffset   // Offset aggiunto alla posizione Y del terminale
                            }
                        });
                    }
                }
            }

            // Lato B - ✅ Rimosso check su cable.collegamentoB per permettere rendering anche se vuoto
            if (cable.terminaleB && terminalBX !== null && terminalBY !== null) {
                const keyB = `${keyB_Conn}|${cable.terminaleB}|B`;
                const existing = siglatureMap.get(keyB);
                const effectiveSectionB = getEffectiveSectionForDisplay(cable.sezione, settings.useAWG);

                if (!existing || getEffectiveSectionForDisplay(existing.cable.sezione, settings.useAWG) < effectiveSectionB) {
                    let siglaturaB = '';
                    if (settings.marcaturePerCollegamento) {
                        siglaturaB = extractSiglaturaFromCollegamento(cable.collegamentoB) || '';
                    } else {
                        siglaturaB = cable.siglatura || '';
                    }

                    // ✅ Verifica che la siglatura sia valida (non placeholder)
                    if (isValidSiglatura(siglaturaB)) {
                        siglatureMap.set(keyB, {
                            cable,
                            siglatura: siglaturaB,
                            side: 'B',
                            index: idx,
                            position: {
                                x: terminalBX + horizontalOffset, // A destra in mobile, stessa X in desktop
                                y: terminalBY + verticalOffset     // Offset aggiunto alla posizione Y del terminale
                            }
                        });
                    }
                }
            }
        });

        return siglatureMap;
    }, [cables, settings, connections, isMobile]);

    return (
        <svg
            className="w-full"
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            onMouseLeave={() => setHoveredCable(null)}
        >
            {/* Renderizza i cavi */}
            {cables.map((cable, idx) => {
                // ✅ Ricostruiamo le chiavi corrette per cercare nella mappa connections
                const isEmptyA = !cable.collegamentoA || String(cable.collegamentoA).trim() === '';
                const keyA = isEmptyA
                    ? `__empty_${cable.originalIndex}_A`
                    : cable.collegamentoA;

                const isEmptyB = !cable.collegamentoB || String(cable.collegamentoB).trim() === '';
                const keyB = isEmptyB
                    ? `__empty_${cable.originalIndex}_B`
                    : cable.collegamentoB;

                const connA = connections[keyA];
                const connB = connections[keyB];

                // Se non ha nessun collegamento, salta
                if (!connA && !connB) return null;

                const cablePos = cablePositions[idx];
                let start = null;
                let end = null;
                let hasIncompleteConnection = false;

                // Gestisci collegamento A
                if (connA) {
                    // ✅ Per i cavi usa connA.x (dove arrivano i cavi), non terminalX
                    start = { x: connA.x, y: isMobile ? connA.y : connA.y + 15 };
                } else if (!isEmptyA) {
                    // Ha un nome ma non è stato trovato nella mappa connections (caso raro se logica è coerente)
                    hasIncompleteConnection = true;
                    start = isMobile
                        ? { x: 50, y: cablePos.y }
                        : { x: cablePos.x - 100, y: 80 };
                }

                // Gestisci collegamento B  
                if (connB) {
                    // ✅ Per i cavi usa connB.x (dove arrivano i cavi), non terminalX
                    end = { x: connB.x, y: isMobile ? connB.y : connB.y + 15 };
                } else if (!isEmptyB) {
                    // Ha un nome ma non è stato trovato nella mappa connections
                    hasIncompleteConnection = true;
                    end = isMobile
                        ? { x: 350, y: cablePos.y }
                        : { x: cablePos.x + 100, y: 80 };
                }

                if (!start || !end) return null;

                const mid = { x: cablePos.x, y: cablePos.y };
                const pathD = (start && end)
                    ? `M ${start.x} ${start.y} 
            L ${isMobile ? mid.x : start.x} ${isMobile ? start.y : mid.y} 
            L ${isMobile ? mid.x : end.x} ${isMobile ? end.y : mid.y} 
            L ${isMobile ? end.x : end.x} ${isMobile ? end.y : end.y}`
                    : null;

                const isHovered = hoveredCable === cable.originalIndex;
                const opacity = hoveredCable !== null && !isHovered ? 0.3 : 1;

                // ✅ Calcola la scala del terminale per questo cavo (come in SingleCableVisualization)
                const calculateTerminalScale = (sezione, useAWG) => {
                    const effectiveSection = getEffectiveSectionForDisplay(sezione, useAWG);

                    // Se la sezione è esattamente nella mappa, usa quel valore
                    if (SECTION_SCALE_MAP[effectiveSection]) {
                        return SECTION_SCALE_MAP[effectiveSection];
                    }

                    // Altrimenti calcola una scala proporzionale
                    const proportionalScale = BASE_TERMINAL_SCALE + (effectiveSection / 100) * TERMINAL_SCALE_PROPORTIONAL_FACTOR;
                    return Math.max(MIN_TERMINAL_SCALE, Math.min(MAX_TERMINAL_SCALE, proportionalScale));
                };

                // Le siglature verranno renderizzate separatamente alla fine per essere sempre in primo piano

                return (
                    <g
                        key={`cable-${idx}`}
                        onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                        onMouseLeave={() => setHoveredCable(null)}
                        style={{ cursor: 'pointer' }}
                    >
                        {pathD && (
                            <CableSVGPath
                                cable={cable}
                                d={pathD}
                                isHovered={isHovered}
                                opacity={opacity}
                                useAWG={settings.useAWG}
                                className={hasIncompleteConnection ? 'stroke-dasharray: 5 5' : ''}
                            />
                        )}
                    </g>
                );
            })}

            {/* Renderizza i terminali */}
            {Object.entries(connections).map(([conn, data]) => {
                // ✅ In mobile usa terminalX (posizione separata), in desktop usa x
                const terminalX = isMobile ? (data.terminalX || data.x) : data.x;
                // ✅ In mobile usa y (stessa Y), in desktop usa terminalY (posizione separata)
                const terminalY = isMobile ? data.y : (data.terminalY || data.y);

                // ✅ Calcola la scala dinamica basata sul cavo con sezione maggiore collegato a questo terminale
                const calculateTerminalScale = (sezione, useAWG) => {
                    const effectiveSection = getEffectiveSectionForDisplay(sezione, useAWG);

                    // Se la sezione è esattamente nella mappa, usa quel valore
                    if (SECTION_SCALE_MAP[effectiveSection]) {
                        return SECTION_SCALE_MAP[effectiveSection];
                    }

                    // Altrimenti calcola una scala proporzionale
                    const proportionalScale = BASE_TERMINAL_SCALE + (effectiveSection / 100) * TERMINAL_SCALE_PROPORTIONAL_FACTOR;
                    return Math.max(MIN_TERMINAL_SCALE, Math.min(MAX_TERMINAL_SCALE, proportionalScale));
                };

                // Trova il cavo con la sezione maggiore tra quelli collegati a questo terminale
                let maxSection = 0;
                let maxSectionCable = null;
                if (data.cables && data.cables.length > 0) {
                    data.cables.forEach(cable => {
                        const effectiveSection = getEffectiveSectionForDisplay(cable.sezione, settings.useAWG);
                        if (effectiveSection > maxSection) {
                            maxSection = effectiveSection;
                            maxSectionCable = cable;
                        }
                    });
                }

                // Calcola la scala basata sul cavo con sezione maggiore, o usa un valore di default
                const terminalScale = maxSectionCable
                    ? calculateTerminalScale(maxSectionCable.sezione, settings.useAWG)
                    : (isMobile ? 0.06 : 0.05); // Fallback al valore precedente se non ci sono cavi

                // ✅ Calcola la posizione del codice del terminale
                // Su mobile: sotto il terminale (come SingleCableVisualization)
                // Su desktop: a destra del terminale
                const terminalCodeX = isMobile
                    ? terminalX  // Stessa X del terminale su mobile
                    : terminalX + (terminalScale * 200 + 5); // A destra su desktop
                const terminalCodeY = isMobile
                    ? terminalY + 25      // 30 pixel sotto su mobile (come SingleCableVisualization)
                    : terminalY;  // Leggermente sotto su desktop
                const terminalCodeAnchor = 'middle';

                return (
                    <g key={`terminal-${conn}`}>
                        {renderTerminalSchematic(
                            data.terminale,
                            terminalX,
                            terminalY,
                            terminalScale,
                            'A',
                            isMobile ? 180 : 270
                        )}
                        {data.terminale && data.terminale !== 'NS' && (
                            <text
                                x={terminalCodeX}
                                y={terminalCodeY}
                                className="text-xs font-medium fill-gray-700"
                                textAnchor={terminalCodeAnchor}
                                transform={`rotate(90, ${terminalCodeX}, ${terminalCodeY})`}
                            >
                                {data.terminale}
                            </text>
                        )}
                        {/* Scritte di collegamento usano data.x (dove arrivano i cavi) */}
                        <text
                            x={isMobile ? data.x - 40 : data.x}
                            y={isMobile ? data.y : data.y - 45}
                            className="text-sm font-bold fill-blue-800"
                            textAnchor={isMobile ? 'end' : 'middle'}
                        >
                            {splitText(data.isEmpty ? '' : (data.collegamento || conn), isMobile ? 8 : 14).map((line, i) => (
                                <tspan
                                    key={i}
                                    x={isMobile ? data.x - 50 : data.x}
                                    dy={i === 0 ? 0 : 16}
                                >
                                    {line}
                                </tspan>
                            ))}
                        </text>
                    </g>
                );
            })}
            {/* ✅ Renderizza tutte le siglature alla fine per essere sempre in primo piano */}
            {Array.from(siglatureToRender.values()).map((siglaturaData, sigIdx) => {
                const { siglatura, position, cable: sigCable, side } = siglaturaData;
                const isHovered = hoveredCable === sigCable.originalIndex;
                const opacity = hoveredCable !== null && !isHovered ? 0.3 : 1;
                const textWidth = estimateTextWidth(siglatura, 12);

                // ✅ In mobile: allineamento a sinistra (a destra del terminale), in desktop: centrato (sotto il terminale)
                const textAnchor = isMobile ? 'start' : 'middle';
                const rectX = isMobile
                    ? position.x - 6  // Inizia dal punto di posizione
                    : position.x - textWidth / 2 - 6; // Centrato

                return (
                    <g key={`siglatura-${sigIdx}`}>
                        <rect
                            x={rectX}
                            y={position.y - 8}
                            width={textWidth + 12}
                            height={16}
                            rx={3}
                            fill="white"
                            stroke="#D1D5DB"
                            strokeWidth={1}
                            filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
                            opacity={opacity}
                        />
                        <text
                            x={position.x}
                            y={position.y + 4}
                            className="text-xs font-bold fill-gray-800"
                            textAnchor={textAnchor}
                            opacity={opacity}
                        >
                            {siglatura}
                        </text>
                    </g>
                );
            })}

            {/* Renderizza le etichette dei numeri e info cavi */}
            {cables.map((cable, idx) => {
                const cablePos = cablePositions[idx];
                return (
                    <g
                        key={`label-${idx}`}
                        onMouseEnter={() => !isMobile && setHoveredCable(cable.originalIndex)}
                        onMouseLeave={() => !isMobile && setHoveredCable(null)}
                        style={{ cursor: 'pointer' }}
                    >
                        {isMobile && (
                            <>
                                <circle
                                    cx={cablePos.x - 20}
                                    cy={cablePos.y}
                                    r={12}
                                    fill="#1F2937"
                                    opacity={0.9}
                                />
                                <text
                                    className="cable-number"
                                    x={cablePos.x - 20}
                                    y={cablePos.y + 5}
                                    fill="white"
                                    textAnchor="middle"
                                >
                                    #{cable.originalIndex + 1}
                                </text>
                                {/* Badge guaina sotto la bolla (mobile) */}
                                {cable.guaina && (() => {
                                    const cableSheath = availableSheaths.find(s => Number(s.id) === Number(cable.guaina));
                                    if (!cableSheath) return null;
                                    const badgeY = cablePos.y + 20;
                                    const badgeText = cableSheath.fullLabel || cableSheath.label;
                                    const badgeWidth = Math.max(60, badgeText.length * 6);
                                    return (
                                        <g>
                                            <rect
                                                x={cablePos.x - 20 - badgeWidth / 2}
                                                y={badgeY}
                                                width={badgeWidth}
                                                height={16}
                                                rx={8}
                                                fill="#e0f2fe"
                                                stroke="#0ea5e9"
                                                strokeWidth={1.5}
                                            />
                                            <text
                                                x={cablePos.x - 20}
                                                y={badgeY + 12}
                                                fontSize="10"
                                                fontWeight="600"
                                                fill="#0c4a6e"
                                                textAnchor="middle"
                                            >
                                                {badgeText}
                                            </text>
                                        </g>
                                    );
                                })()}
                            </>
                        )}
                        {!isMobile && (
                            <>
                                <circle
                                    cx={cablePos.x}
                                    cy={cablePos.y - 20}
                                    r={12}
                                    fill="#1F2937"
                                    opacity={0.9}
                                />
                                <text
                                    className="cable-number"
                                    x={cablePos.x}
                                    y={cablePos.y - 15}
                                    fill="white"
                                    textAnchor="middle"
                                >
                                    #{cable.originalIndex + 1}
                                </text>
                                {/* Badge guaina sotto la bolla (desktop) */}
                                {cable.guaina && (() => {
                                    const cableSheath = availableSheaths.find(s => Number(s.id) === Number(cable.guaina));
                                    if (!cableSheath) return null;
                                    const badgeY = cablePos.y - 20 + 20;
                                    const badgeText = cableSheath.fullLabel || cableSheath.label;
                                    const badgeWidth = Math.max(60, badgeText.length * 6);
                                    return (
                                        <g>
                                            <rect
                                                x={cablePos.x - badgeWidth / 2}
                                                y={badgeY + 10}
                                                width={badgeWidth}
                                                height={16}
                                                rx={8}
                                                fill="#e0f2fe"
                                                stroke="#0ea5e9"
                                                strokeWidth={1.5}
                                            />
                                            <text
                                                x={cablePos.x}
                                                y={badgeY + 22}
                                                fontSize="10"
                                                fontWeight="600"
                                                fill="#0c4a6e"
                                                textAnchor="middle"
                                            >
                                                {badgeText}
                                            </text>
                                        </g>
                                    );
                                })()}
                            </>
                        )}
                        {isMobile && (
                            <g>
                                <rect
                                    x={cablePos.x + 25}
                                    y={cablePos.y - 15}
                                    width={170}
                                    height={30}
                                    rx={3}
                                    fill="white"
                                    stroke="#E5E7EB"
                                    strokeWidth={1}
                                    opacity={0.95}
                                />
                                <text
                                    x={cablePos.x + 30}
                                    y={cablePos.y + 5}
                                    className="text-xs fill-gray-600"
                                    textAnchor="start"
                                >
                                    {`${cable.colore} • ${formatSezione(cable.sezione, settings.useAWG)} • ${cable.lunghezza}mm`}
                                </text>
                            </g>
                        )}
                    </g>
                );
            })}

            {/* Tooltip solo per desktop */}
            {!isMobile && cables.map((cable, idx) => {
                const isHovered = hoveredCable === cable.originalIndex;
                const tooltipX = svgWidth / 2 - 125;
                const tooltipY = 130;
                return isHovered && (
                    <g key={`tooltip-${idx}`}>
                        <rect
                            x={tooltipX}
                            y={tooltipY}
                            width={250}
                            height={60}
                            rx={5}
                            fill="white"
                            stroke="#E5E7EB"
                            strokeWidth={1}
                            filter="drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))"
                        />
                        <text
                            x={tooltipX + 125}
                            y={tooltipY + 20}
                            className="text-xs font-semibold fill-gray-800"
                            textAnchor="middle"
                        >
                            {`Cavo #${cable.originalIndex + 1}: ${cable.siglatura}`}
                        </text>
                        <text
                            x={tooltipX + 125}
                            y={tooltipY + 35}
                            className="text-xs fill-gray-600"
                            textAnchor="middle"
                        >
                            {`${cable.colore} • ${formatSezione(cable.sezione, settings.useAWG)} • ${cable.lunghezza}mm`}
                        </text>
                        <text
                            x={tooltipX + 125}
                            y={tooltipY + 50}
                            className="text-xs fill-gray-600"
                            textAnchor="middle"
                        >
                            {`${cable.collegamentoA || '—'} → ${cable.collegamentoB || '—'}`}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
};

export default MultipleCableVisualization;